import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { sanityClient, urlFor } from "../lib/sanityClient";
import "../styles/TitleBar.css";

const QUERY = `*[_type == "siteSettings"][0]{
  logoDarkDesktop, logoDarkMobile, logoLightDesktop, logoLightMobile,
  arrowIcon, hamburgerIcon, ctaText, ctaUrl, navItems
}`;

const TitleBar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isDesktop, setIsDesktop] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    sanityClient.fetch(QUERY).then(setData);
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "instant" });
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const titlebar = document.querySelector(".titlebar");
      if (!titlebar) return;
      
      const sections = document.querySelectorAll("[data-theme]");
      const navbarHeight = titlebar.offsetHeight || 0;
      
      let current = "";
      for (let section of sections) {
        const rect = section.getBoundingClientRect();
        if (rect.top <= navbarHeight + 50 && rect.bottom > navbarHeight + 50) {
          const id = section.getAttribute("id");
          current = id;

          const theme = section.getAttribute("data-theme");
          if (theme === "light") {
            titlebar.classList.add("light-theme");
          } else {
            titlebar.classList.remove("light-theme");
          }
          break;
        }
      }
      setActiveSection(current);
    };

    const handleResize = () => {
      setIsDesktop(window.innerWidth > 1024);
    };

    handleScroll();
    handleResize();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  if (!data) return null;

  const navItems = data.navItems || [];

  const logoDark = isDesktop
    ? urlFor(data.logoDarkDesktop).url()
    : urlFor(data.logoDarkMobile).url();

  const logoLight = isDesktop
    ? urlFor(data.logoLightDesktop).url()
    : urlFor(data.logoLightMobile).url();

  return (
    <header className="titlebar">
      <motion.div
        className="titlebar-content"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="logo-nav-group">
          <div
            className="logo-container"
            onClick={() => window.location.reload()}
            style={{ cursor: "pointer" }}
          >
            <img src={logoDark} alt="Logo Dark" className="logo logo-dark" />
            <img src={logoLight} alt="Logo Light" className="logo logo-light" />
          </div>

          <nav className={`nav-links ${menuOpen ? "active" : ""}`}>
            {navItems.map(({ label, href, _key }) => (
              <motion.a
                key={_key}
                href={href}
                className={`nav-clean-hover nav-item ${
                  activeSection === href.substring(1) ? "active-link" : ""
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const target = document.querySelector(href);
                  if (target) {
                    target.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                    setMenuOpen(false);
                  }
                }}
                whileTap={{ scale: 0.95 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                }}
              >
                {label}
              </motion.a>
            ))}
          </nav>
        </div>

        <motion.button
          className="cta-button"
          onClick={() => window.open(data.ctaUrl, "_blank")}
          whileHover="hover"
          whileTap={{ scale: 0.95 }}
          variants={{ hover: { scale: 1.05 } }}
          transition={{ type: "spring", stiffness: 150, damping: 15 }}
        >
          <motion.span
            className="cta-text"
            variants={{ hover: { scale: 1.03 } }}
            transition={{ type: "spring", stiffness: 400, damping: 15 }}
          >
            {data.ctaText}
          </motion.span>
          <motion.span
            className="arrow-icon"
            variants={{
              initial: { rotate: 0 },
              hover: { rotate: 45 },
            }}
            initial="initial"
            animate="initial"
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <img src={urlFor(data.arrowIcon).url()} alt="arrow icon" />
          </motion.span>
        </motion.button>

        <button className="hamburger" onClick={() => setMenuOpen(!menuOpen)}>
          <img
            src={urlFor(data.hamburgerIcon).url()}
            alt="Menu"
            className="hamburger-icon"
          />
        </button>

        {menuOpen && (
          <button className="close-button" onClick={() => setMenuOpen(false)}>
            ✕
          </button>
        )}
      </motion.div>
    </header>
  );
};

export default TitleBar;
